import { db } from '../../config/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
  Unsubscribe,
  writeBatch
} from 'firebase/firestore';
import { AuditService } from './AuditService';

export interface Patient {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  emergencyContact?: {
    name: string;
    relationship: string;
    phone: string;
    email?: string;
  };
  insurance?: {
    provider: string;
    policyNumber: string;
    groupNumber?: string;
    subscriberName?: string;
  };
  medicalHistory?: string;
  allergies?: string;
  currentMedications?: string;
  pharmacy?: {
    name: string;
    phone: string;
    fax?: string;
    address?: string;
    email?: string;
  };
  preferredLanguage?: string;
  maritalStatus?: string;
  occupation?: string;
  referralSource?: string;
  status: 'active' | 'inactive' | 'discharged' | 'archived';
  assignedProviderId: string;
  intakeDate?: string;
  presentingConcern?: string;
  chiefComplaint?: string;
  isEstablished?: boolean;  // Skip initial consultation (e.g., transferred patients)
  isMinor?: boolean;
  guardianInfo?: {
    name: string;
    relationship: string;
    phone: string;
    email?: string;
  };
  createdAt: Date;
  updatedAt: Date;
}

export class FirebasePatientService {
  private static COLLECTION = 'patients';

  /**
   * Get all active patients for a specific provider.
   * Firestore security rules enforce that a provider can only read their own patients.
   * @param providerId - The UID of the logged-in provider
   */
  static async getAllPatients(providerId: string): Promise<Patient[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION),
        where('assignedProviderId', '==', providerId),
        where('status', '!=', 'archived'),
        orderBy('status'),
        orderBy('lastName', 'asc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => this.convertFromFirestore(d.id, d.data()));
    } catch (error) {
      console.error('Error fetching patients:', error);
      throw error;
    }
  }

  /**
   * Get all patients across the entire practice (for System Admin / Clinical Director).
   * No provider filter — returns every non-archived patient.
   */
  static async getAllPracticePatients(): Promise<Patient[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION),
        where('status', '!=', 'archived'),
        orderBy('status'),
        orderBy('lastName', 'asc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => this.convertFromFirestore(d.id, d.data()));
    } catch (error) {
      console.error('Error fetching all practice patients:', error);
      throw error;
    }
  }

  /**
   * Get all unassigned patients (those who self-registered without an invite).
   * These have assignedProviderId == '' and can be claimed by any provider.
   */
  static async getUnassignedPatients(): Promise<Patient[]> {
    try {
      console.log('[DEBUG] getUnassignedPatients: querying patients where assignedProviderId == ""');
      const q = query(
        collection(db, this.COLLECTION),
        where('assignedProviderId', '==', '')
      );
      const snapshot = await getDocs(q);
      console.log('[DEBUG] getUnassignedPatients: found', snapshot.size, 'documents');
      snapshot.docs.forEach(d => {
        const data = d.data();
        console.log('[DEBUG] patient doc:', d.id, 'assignedProviderId:', JSON.stringify(data.assignedProviderId), 'status:', data.status, 'name:', data.firstName, data.lastName);
      });
      return snapshot.docs
        .map(d => this.convertFromFirestore(d.id, d.data()))
        .filter(p => p.status !== 'archived');
    } catch (error) {
      console.error('[DEBUG] Error fetching unassigned patients:', error);
      return [];
    }
  }

  /**
   * Get single patient by ID
   */
  static async getPatient(patientId: string, accessedBy?: string): Promise<Patient | null> {
    try {
      const docRef = doc(db, this.COLLECTION, patientId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const patient = this.convertFromFirestore(docSnap.id, docSnap.data());
        // Audit: log PHI access
        if (accessedBy) {
          AuditService.logPhiAccess(accessedBy, 'provider', 'patient', patientId, patientId, 'Viewed patient record');
        }
        return patient;
      }
      return null;
    } catch (error) {
      console.error('Error fetching patient:', error);
      return null;
    }
  }

  /**
   * Find a patient record by email address.
   * Used to link email-login clients to provider-created patient records.
   */
  static async getPatientByEmail(email: string): Promise<Patient | null> {
    try {
      // Try exact match first, then lowercase (Firestore is case-sensitive)
      for (const emailVariant of [email, email.toLowerCase()]) {
        const q = query(
          collection(db, this.COLLECTION),
          where('email', '==', emailVariant)
        );
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          const docSnap = snapshot.docs[0];
          return this.convertFromFirestore(docSnap.id, docSnap.data());
        }
      }
      return null;
    } catch (error) {
      console.error('Error finding patient by email:', error);
      return null;
    }
  }

  /**
   * Create new patient.
   * assignedProviderId must be a string (can be '' for self-registered clients).
   * Firestore security rules enforce that non-empty values match the authenticated user.
   */
  static async createPatient(
    patientData: Omit<Patient, 'id' | 'createdAt' | 'updatedAt'>,
    documentId?: string
  ): Promise<string> {
    try {
      if (patientData.assignedProviderId === undefined || patientData.assignedProviderId === null) {
        throw new Error('assignedProviderId is required (use empty string for unassigned)');
      }

      // Use provided documentId (e.g. Auth UID for self-registration) or auto-generate
      const docRef = documentId
        ? doc(db, this.COLLECTION, documentId)
        : doc(collection(db, this.COLLECTION));

      const newPatient: Record<string, any> = {
        ...patientData,
        status: patientData.status || 'active',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      // Firestore does not accept undefined values — strip them out
      Object.keys(newPatient).forEach(key => {
        if (newPatient[key] === undefined) {
          delete newPatient[key];
        }
        // Also clean nested objects (e.g. insurance, emergencyContact)
        if (newPatient[key] && typeof newPatient[key] === 'object' && !(newPatient[key] instanceof Timestamp)) {
          Object.keys(newPatient[key]).forEach(subKey => {
            if (newPatient[key][subKey] === undefined) {
              delete newPatient[key][subKey];
            }
          });
        }
      });

      console.log('[DEBUG] createPatient: writing to', docRef.path, 'assignedProviderId:', JSON.stringify(newPatient.assignedProviderId));
      await setDoc(docRef, newPatient);
      console.log('[DEBUG] createPatient: SUCCESS — doc created at', docRef.path);
      // Audit: log patient creation
      const auditUserId = patientData.assignedProviderId || docRef.id;
      const auditRole = patientData.assignedProviderId ? 'provider' : 'client';
      AuditService.logPhiModification(
        auditUserId, auditRole as any, 'create', 'patient', docRef.id, docRef.id, 'Created patient record'
      );
      return docRef.id;
    } catch (error) {
      console.error('Error creating patient:', error);
      throw error;
    }
  }

  /**
   * Update patient
   */
  static async updatePatient(patientId: string, updates: Partial<Patient>): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION, patientId);

      const { id, createdAt, ...updateData } = updates as any;

      await updateDoc(docRef, {
        ...updateData,
        updatedAt: Timestamp.now()
      });
      // Audit: log patient update
      const changedFields = Object.keys(updateData).filter(k => k !== 'updatedAt').join(', ');
      AuditService.logPhiModification(
        updates.assignedProviderId || 'unknown', 'provider', 'update', 'patient', patientId, patientId,
        `Updated fields: ${changedFields}`
      );
    } catch (error) {
      console.error('Error updating patient:', error);
      throw error;
    }
  }

  /**
   * Archive patient (soft delete — HIPAA compliant).
   * Hard deletes are blocked by Firestore security rules.
   */
  static async archivePatient(patientId: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION, patientId);

      await updateDoc(docRef, {
        status: 'archived',
        updatedAt: Timestamp.now()
      });
      // Audit: log patient archival (soft delete)
      AuditService.logPhiModification(
        'provider', 'provider', 'delete', 'patient', patientId, patientId, 'Patient record archived (soft delete)'
      );
    } catch (error) {
      console.error('Error archiving patient:', error);
      throw error;
    }
  }

  /**
   * Search patients by name or email (scoped to a provider).
   */
  static async searchPatients(providerId: string, searchTerm: string): Promise<Patient[]> {
    try {
      const allPatients = await this.getAllPatients(providerId);

      const term = searchTerm.toLowerCase();
      return allPatients.filter(patient =>
        patient.firstName.toLowerCase().includes(term) ||
        patient.lastName.toLowerCase().includes(term) ||
        patient.email.toLowerCase().includes(term)
      );
    } catch (error) {
      console.error('Error searching patients:', error);
      return [];
    }
  }

  /**
   * Subscribe to real-time patient updates (scoped to a provider).
   * Returns unsubscribe function.
   */
  static subscribeToPatients(
    providerId: string,
    callback: (patients: Patient[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, this.COLLECTION),
      where('assignedProviderId', '==', providerId),
      where('status', '!=', 'archived'),
      orderBy('status'),
      orderBy('lastName', 'asc')
    );

    return onSnapshot(q, (snapshot) => {
      const patients = snapshot.docs.map(d => this.convertFromFirestore(d.id, d.data()));
      callback(patients);
    });
  }

  /**
   * Subscribe to single patient updates
   */
  static subscribeToPatient(
    patientId: string,
    callback: (patient: Patient | null) => void
  ): Unsubscribe {
    const docRef = doc(db, this.COLLECTION, patientId);

    return onSnapshot(docRef, (snap) => {
      if (snap.exists()) {
        callback(this.convertFromFirestore(snap.id, snap.data()));
      } else {
        callback(null);
      }
    });
  }

  /**
   * Transfer a patient to a new provider — updates assignedProviderId on the patient
   * AND migrates providerId on ALL clinical records so the new provider has full access.
   * Collections updated: diagnoses, prescriptions, clinical_notes, treatment_plans,
   * homework, psychotherapy_notes, sessions, session_recordings, client_files, couples,
   * form_assignments.
   */
  static async transferPatientToProvider(
    patientId: string,
    oldProviderId: string,
    newProviderId: string,
    newProviderName?: string
  ): Promise<{ updatedCount: number }> {
    const CLINICAL_COLLECTIONS = [
      { name: 'diagnoses', patientField: 'patientId', providerNameField: 'diagnosedBy' },
      { name: 'prescriptions', patientField: 'patientId', providerNameField: 'prescribedBy' },
      { name: 'clinical_notes', patientField: 'patientId', providerNameField: null },
      { name: 'treatment_plans', patientField: 'patientId', providerNameField: null },
      { name: 'homework', patientField: 'clientId', providerNameField: null },
      { name: 'psychotherapy_notes', patientField: 'patientId', providerNameField: null },
      { name: 'sessions', patientField: 'patientId', providerNameField: null },
      { name: 'session_recordings', patientField: 'patientId', providerNameField: null },
      { name: 'client_files', patientField: 'clientId', providerNameField: null },
      { name: 'couples', patientField: null, providerNameField: null },
      { name: 'form_assignments', patientField: 'clientId', providerNameField: null },
      { name: 'form_responses', patientField: 'clientId', providerNameField: null },
    ];

    let totalUpdated = 0;

    try {
      for (const col of CLINICAL_COLLECTIONS) {
        // Build query: find records for this patient from the old provider
        let q;
        if (col.name === 'couples') {
          // Couples: match by providerId + either patient slot
          q = query(
            collection(db, col.name),
            where('providerId', '==', oldProviderId)
          );
        } else {
          q = query(
            collection(db, col.name),
            where(col.patientField!, '==', patientId),
            where('providerId', '==', oldProviderId)
          );
        }

        const snapshot = await getDocs(q);
        if (snapshot.empty) continue;

        // Firestore batches max 500 writes — split if needed
        const docs = snapshot.docs;
        for (let i = 0; i < docs.length; i += 450) {
          const batch = writeBatch(db);
          const chunk = docs.slice(i, i + 450);

          for (const docSnap of chunk) {
            const updates: Record<string, any> = {
              providerId: newProviderId,
              updatedAt: Timestamp.now(),
            };

            // For couples, filter: only include if this patient is in the couple
            if (col.name === 'couples') {
              const data = docSnap.data();
              if (data.patient1Id !== patientId && data.patient2Id !== patientId) {
                continue;
              }
            }

            batch.update(doc(db, col.name, docSnap.id), updates);
            totalUpdated++;
          }

          await batch.commit();
        }
      }

      // Update the patient record — mark as established so they skip initial consultation
      await this.updatePatient(patientId, { assignedProviderId: newProviderId, isEstablished: true });

      // Audit the transfer
      AuditService.logPhiModification(
        oldProviderId, 'provider', 'update', 'patient', patientId, patientId,
        `Patient transferred from provider ${oldProviderId} to ${newProviderId}. ${totalUpdated} clinical records migrated.`
      );

      console.log(`[Transfer] Patient ${patientId}: ${totalUpdated} records migrated to provider ${newProviderId}`);
      return { updatedCount: totalUpdated };
    } catch (error) {
      console.error('Error transferring patient records:', error);
      throw error;
    }
  }

  /**
   * Helper: Convert Firestore data to Patient object
   */
  private static convertFromFirestore(id: string, data: any): Patient {
    return {
      id,
      firstName: data.firstName,
      lastName: data.lastName,
      dateOfBirth: data.dateOfBirth,
      email: data.email,
      phone: data.phone,
      address: data.address,
      city: data.city,
      state: data.state,
      zipCode: data.zipCode,
      emergencyContact: data.emergencyContact,
      insurance: data.insurance,
      medicalHistory: data.medicalHistory,
      allergies: data.allergies,
      currentMedications: data.currentMedications,
      preferredLanguage: data.preferredLanguage,
      maritalStatus: data.maritalStatus,
      occupation: data.occupation,
      referralSource: data.referralSource,
      status: data.status || 'active',
      assignedProviderId: data.assignedProviderId,
      intakeDate: data.intakeDate,
      presentingConcern: data.presentingConcern,
      chiefComplaint: data.chiefComplaint,
      isMinor: data.isMinor,
      guardianInfo: data.guardianInfo,
      isEstablished: data.isEstablished || false,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    };
  }
}
